import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLightbulb, FiTarget, FiHeart, FiTrendingUp } = FiIcons;

const Tips = () => {
  const tips = [
    {
      icon: FiTarget,
      title: 'Know Your Audience',
      content: 'Write for your specific readers. Christian romance readers have different expectations than devotional readers. Use language and themes that resonate with your target audience.',
      color: 'from-blue-500 to-purple-600'
    },
    {
      icon: FiHeart,
      title: 'Lead with Emotion',
      content: 'Start with the emotional hook that will make readers care about your book. What feeling or need does your book address? Lead with that pain point or desire.',
      color: 'from-pink-500 to-red-600'
    },
    {
      icon: FiTrendingUp,
      title: 'Show the Transformation',
      content: 'Clearly communicate how readers will be different after reading your book. What will they learn, feel, or be able to do? Focus on benefits, not just features.',
      color: 'from-green-500 to-blue-600'
    },
    {
      icon: FiLightbulb,
      title: 'Keep It Scannable',
      content: 'Use short paragraphs, bullet points when appropriate, and compelling language. Online readers scan quickly, so make every word count and break up large blocks of text.',
      color: 'from-yellow-500 to-orange-600'
    }
  ];

  return (
    <motion.div
      className="max-w-6xl mx-auto mt-16"
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.3 }}
    >
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-gray-800 mb-4">
          Pro Tips for Better Book Descriptions
        </h2>
        <p className="text-lg text-gray-600">
          Follow these guidelines to create descriptions that convert browsers into buyers
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {tips.map((tip, index) => (
          <motion.div
            key={index}
            className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            whileHover={{ y: -5 }}
          >
            <div className={`h-2 bg-gradient-to-r ${tip.color}`}></div>
            
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className={`p-3 rounded-full bg-gradient-to-r ${tip.color} text-white`}>
                  <SafeIcon icon={tip.icon} className="text-xl" />
                </div>
                <h3 className="font-bold text-gray-800">{tip.title}</h3>
              </div>
              
              <p className="text-gray-600 text-sm leading-relaxed">
                {tip.content}
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-8 mt-12 text-white text-center"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.5 }}
      >
        <h3 className="text-2xl font-bold mb-4">Remember: Your Description is Your Sales Page</h3>
        <p className="text-purple-100 max-w-3xl mx-auto">
          Your book description isn't just a summary—it's a sales tool. It should create curiosity, 
          establish your authority, and compel readers to click "Buy Now." Focus on benefits over features, 
          and always keep your reader's needs at the center of your message.
        </p>
      </motion.div>
    </motion.div>
  );
};

export default Tips;